import React from 'react'
import { useNavigate } from 'react-router-dom'
import { signOut } from 'supertokens-web-js/recipe/emailpassword'
import { Search, Bell, LogOut, ChevronDown, Settings, Flag } from 'lucide-react'

import { Avatar, AvatarFallback } from './ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu'
import { Button } from './ui/button'
import { useUser } from '@renderer/context/UserContext'
import { openReportIssue } from '@renderer/lib/reportIssue'

const HEADER_CLASS =
  'sticky top-0 z-30 h-[78px] min-h-[78px] px-3 sm:px-4 md:px-6 bg-[#faf9f5]/85 backdrop-blur-xl border-b border-[#775A00]/10'

interface HeaderProps {
  userEmail?: string
}

export const Header: React.FC<HeaderProps> = ({ userEmail }) => {
  const navigate = useNavigate()
  const { canReportIssues } = useUser()

  const handleLogout = async (): Promise<void> => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const displayName = userEmail ?? ''
  const userInitials = userEmail?.charAt(0).toUpperCase() || 'U'

  if (!userEmail) {
    return (
      <header className={`${HEADER_CLASS} flex items-center justify-between`}>
        <div className="h-4 w-32 rounded-full bg-[#775A00]/10 animate-pulse" />
        <div className="flex items-center gap-3">
          <div className="hidden md:block h-9 w-48 lg:w-64 rounded-full bg-[#775A00]/10 animate-pulse" />
          <div className="h-9 w-9 rounded-full bg-[#775A00]/10 animate-pulse" />
          <div className="h-10 w-10 rounded-full bg-[#775A00]/10 animate-pulse" />
        </div>
      </header>
    )
  }

  return (
    <header
      className={`${HEADER_CLASS} flex items-center justify-between gap-3 md:gap-4`}
      style={{ color: '#20242f', lineHeight: 1.6 }}
    >
      <div className="flex min-w-0 flex-1 justify-center">
        <div className="flex h-9 w-full min-w-0 max-w-[16rem] items-center rounded-full lg:max-w-[18rem] border border-[#775A00]/20 bg-white/80 overflow-hidden transition-all duration-200 hover:border-[#775A00]/35 focus-within:border-[#775A00]/45 focus-within:bg-white focus-within:ring-2 focus-within:ring-[#775A00]/10 focus-within:shadow-sm">
          <input
            type="text"
            aria-label="Search resources"
            placeholder="Search"
            className="w-full h-full px-4 bg-transparent text-sm text-[#5e5d59] placeholder-[#5e5d59]/50 focus:outline-none"
          />
          <div className="flex items-center justify-center h-full px-3 border-l border-[#775A00]/15 text-[#775A00]/70">
            <Search size={17} />
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1 sm:gap-3 justify-end">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          className="relative rounded-full text-[#735C00] hover:bg-[#775A00]/10 hover:text-[#5c4700] focus-visible:ring-2 focus-visible:ring-[#775A00]/25 [&_svg]:size-[18px]"
        >
          <Bell />
        </Button>

        <div className="hidden sm:block h-7 w-px bg-[#775A00]/15" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="group h-auto max-w-[220px] rounded-full p-1 lg:pl-3 lg:pr-1.5 lg:py-1.5 gap-2 hover:bg-[#775A00]/10 focus-visible:ring-2 focus-visible:ring-[#775A00]/25 data-[state=open]:bg-[#775A00]/10"
            >
              <span className="hidden lg:block min-w-0 truncate text-sm font-medium text-[#4A3B12]">
                {displayName}
              </span>
              <ChevronDown className="hidden lg:block text-[#4A3B12]/50 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              <Avatar className="h-9 w-9 ring-1 ring-[#775A00]/15">
                <AvatarFallback className="bg-[#775A00]/12 text-[#4A3B12] text-xs font-semibold tracking-wide">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-60 rounded-xl border-[#775A00]/10 bg-white p-1.5 shadow-lg shadow-[#775A00]/5"
            align="end"
            sideOffset={8}
            forceMount
          >
            <DropdownMenuLabel className="font-normal px-2 py-2">
              <div className="flex flex-col space-y-1 min-w-0">
                <p className="text-sm font-medium leading-none text-[#20242f] truncate">
                  {userEmail}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#775A00]/10" />
            <DropdownMenuItem
              onClick={() => navigate('/settings')}
              className="rounded-lg px-2 py-2 text-sm text-gray-700 cursor-pointer focus:bg-[#775A00]/10 focus:text-[#4A3B12]"
            >
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            {canReportIssues && (
              <DropdownMenuItem
                onClick={openReportIssue}
                className="rounded-lg px-2 py-2 text-sm text-gray-700 cursor-pointer focus:bg-[#775A00]/10 focus:text-[#4A3B12]"
              >
                <Flag className="mr-2 h-4 w-4" />
                Report an issue
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-[#775A00]/10" />
            <DropdownMenuItem
              onClick={handleLogout}
              className="rounded-lg px-2 py-2 text-sm text-red-600 cursor-pointer focus:bg-red-50 focus:text-red-700"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
